import { anthropic } from "@ai-sdk/anthropic";
import { google } from "@ai-sdk/google";
import { createOpenAI } from "@ai-sdk/openai";
import type { FluentifyRequest, FluentifyResponse } from "@fluentry/domain";
import * as v from "valibot";
import {
  classifyRequestComplexity,
  generateStructuredOutputWithRetry,
  isModelOutputInvalidError,
  ModelOutputInvalidError,
  type RequestComplexity,
} from "../ai/structured-output";
import { createLogger } from "../logging/logger";
import {
  LLMProviderRouter,
  type LLMProvider,
  type LLMRoutingPriority,
  type LLMTask,
} from "./router";

const logger = createLogger({ context: { scope: "fluentify.llm" } });

const FluentryResponseSchema = v.object({
  casual: v.pipe(v.string(), v.minLength(1)),
  formal: v.pipe(v.string(), v.minLength(1)),
  conversational: v.pipe(v.string(), v.minLength(1)),
  confidence: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(1))),
  explanation: v.optional(v.string()),
});

const PhoneticCandidateSchema = v.object({
  word: v.pipe(v.string(), v.minLength(1)),
  confidence: v.pipe(v.number(), v.minValue(0), v.maxValue(1)),
  meaning: v.pipe(v.string(), v.minLength(1)),
  reading: v.optional(v.string()),
  reason: v.optional(v.string()),
});

const PhoneticResolveModelSchema = v.object({
  candidates: v.pipe(v.array(PhoneticCandidateSchema), v.minLength(3), v.maxLength(12)),
});

const EditorAssistAlternativeSchema = v.object({
  text: v.pipe(v.string(), v.minLength(1)),
  confidence: v.pipe(v.number(), v.minValue(0), v.maxValue(1)),
  nativeExplanation: v.pipe(v.string(), v.minLength(1)),
});

const EditorAssistResultSchema = v.object({
  alternatives: v.pipe(v.array(EditorAssistAlternativeSchema), v.minLength(1), v.maxLength(12)),
});

export interface LLMConfig {
  provider?: LLMProvider | "auto";
  priority?: LLMRoutingPriority;
  openaiApiKey?: string;
  openaiModel?: string;
  gatewayBaseURL?: string;
  gatewayAuthToken?: string;
  zaiApiKey?: string;
  zaiBaseURL?: string;
  zaiModel?: string;
  env?: Record<string, any>;
}

export interface PhoneticResolveRequest {
  input: string;
  sourceLanguage: string;
  targetLanguage: string;
  context?: string;
  uiLanguage?: string;
  priority?: LLMRoutingPriority;
}

export interface PhoneticResolveCandidate {
  word: string;
  confidence: number;
  meaning: string;
  reading?: string;
  reason?: string;
}

export interface PhoneticResolveResponse {
  task: "phonetic_resolve";
  candidates: PhoneticResolveCandidate[];
  top1: PhoneticResolveCandidate;
  meta: {
    sourceLanguage: string;
    targetLanguage: string;
    usedContext: boolean;
    provider: LLMProvider;
    requestComplexity: RequestComplexity;
  };
}

export type EditorAssistCommand = "rewrite" | "summarize" | "translate" | "tone-change";

export interface EditorAssistRequest {
  command: EditorAssistCommand;
  text: string;
  targetLanguage: string;
  nativeLanguage: string;
  selection?: {
    start?: number;
    end?: number;
  };
  options?: {
    tone?: "formal" | "casual" | "neutral";
    length?: "short" | "same" | "long";
    maxAlternatives?: number;
  };
}

export interface EditorAssistAlternative {
  text: string;
  confidence: number;
  nativeExplanation: string;
}

export interface EditorAssistResponse {
  command: EditorAssistCommand;
  alternatives: EditorAssistAlternative[];
  meta: {
    provider: LLMProvider;
    requestComplexity: RequestComplexity;
  };
}

type ServiceTask = LLMTask | "editor_assist";

export class FluentifyLLMService {
  private readonly systemPrompt = `You are Fluentry, a language learning assistant that converts mixed-language input into natural target language output.

Your task:
1. Detect mixed-language segments in the input.
2. Convert the entire input to fluent, natural target language.
3. Provide exactly 3 variations: casual, formal, conversational.
4. Keep meaning and intent unchanged.

Output must be provided through tool calling only.`;

  private readonly phoneticSystemPrompt = `You are Fluentry Phonetic Context Resolver.

Your task:
1. Interpret ambiguous phonetic input and infer likely canonical words in the target language.
2. Use source language hints and optional context sentence to rank plausible candidates.
3. Return diverse candidates with confidence values between 0 and 1.

Output must be provided through tool calling only.`;

  private readonly editorAssistSystemPrompt = `You are Fluentry Editor Assist.

Your task:
1. Generate alternatives for the requested command.
2. Keep alternatives safe, clear, and natural in target language.
3. Provide native-language explanation for each alternative.
4. Confidence must be 0..1.

Output must be provided through tool calling only.`;

  private runtimeEnv?: Record<string, any>;
  private readonly router: LLMProviderRouter;

  constructor(private config: LLMConfig = {}) {
    this.router = new LLMProviderRouter({
      isProviderEnabled: (provider) => this.isProviderEnabled(provider),
    });
  }

  setRuntimeEnv(env?: Record<string, any>) {
    this.runtimeEnv = env;
  }

  async convertText(
    request: FluentifyRequest,
    options: { temperature?: number; includeExplanation?: boolean; priority?: LLMRoutingPriority } = {},
  ): Promise<FluentifyResponse> {
    const { targetLanguage = "en" } = request;
    const { temperature = 0.7, includeExplanation = false, priority } = options;

    const execution = await this.executeWithRouter({
      task: "translate",
      sourceLanguage: "mixed",
      targetLanguage,
      hasContext: false,
      priority,
      temperature,
      maxTokens: 900,
      inputText: request.text,
      systemPrompt: this.buildTranslateSystemPrompt(targetLanguage, includeExplanation),
      userPrompt: request.text,
      toolName: "submit_translate_result",
      toolDescription: "Submit translation variants in strict schema.",
      schema: FluentryResponseSchema,
    });

    const parsed = execution.parsed;

    return {
      original: request.text,
      candidates: [
        { text: parsed.casual, style: "casual", confidence: parsed.confidence || 0.9 },
        { text: parsed.formal, style: "formal", confidence: parsed.confidence || 0.9 },
        {
          text: parsed.conversational,
          style: "conversational",
          confidence: parsed.confidence || 0.9,
        },
      ],
      confidence: parsed.confidence || 0.9,
      explanation: parsed.explanation,
    };
  }

  async convertTextWithFallback(
    request: FluentifyRequest,
    options: { temperature?: number; includeExplanation?: boolean; priority?: LLMRoutingPriority } = {},
  ): Promise<FluentifyResponse> {
    return this.convertText(request, options);
  }

  async resolvePhonetic(
    request: PhoneticResolveRequest,
    options: { temperature?: number; priority?: LLMRoutingPriority } = {},
  ): Promise<PhoneticResolveResponse> {
    const { temperature = 0.1, priority } = options;
    const normalizedInput = this.normalizePhoneticInput(request.input);
    const contextText = request.context?.trim() || "";

    const execution = await this.executeWithRouter({
      task: "phonetic_resolve",
      sourceLanguage: request.sourceLanguage,
      targetLanguage: request.targetLanguage,
      hasContext: Boolean(contextText),
      priority: request.priority || priority,
      temperature,
      maxTokens: 1000,
      inputText: normalizedInput,
      contextText,
      systemPrompt: this.buildPhoneticSystemPrompt(request),
      userPrompt: normalizedInput,
      toolName: "submit_phonetic_candidates",
      toolDescription: "Submit phonetic resolution candidates in strict schema.",
      schema: PhoneticResolveModelSchema,
    });

    const candidates = this.postProcessPhoneticCandidates(execution.parsed.candidates);
    if (candidates.length === 0) {
      throw new Error("No phonetic candidates");
    }

    return {
      task: "phonetic_resolve",
      candidates,
      top1: candidates[0],
      meta: {
        sourceLanguage: request.sourceLanguage,
        targetLanguage: request.targetLanguage,
        usedContext: Boolean(contextText),
        provider: execution.provider,
        requestComplexity: execution.requestComplexity,
      },
    };
  }

  async resolvePhoneticWithFallback(
    request: PhoneticResolveRequest,
    options: { temperature?: number; priority?: LLMRoutingPriority } = {},
  ): Promise<PhoneticResolveResponse> {
    return this.resolvePhonetic(request, options);
  }

  async assistEditor(
    request: EditorAssistRequest,
    options: { temperature?: number; priority?: LLMRoutingPriority } = {},
  ): Promise<EditorAssistResponse> {
    const { temperature = 0.5, priority } = options;
    const maxAlternatives = Math.max(1, Math.min(request.options?.maxAlternatives || 3, 12));

    const execution = await this.executeWithRouter({
      task: "editor_assist",
      sourceLanguage: request.nativeLanguage,
      targetLanguage: request.targetLanguage,
      hasContext: Boolean(request.selection),
      priority,
      temperature,
      maxTokens: 900,
      inputText: request.text,
      contextText: request.command,
      maxAlternatives,
      systemPrompt: this.buildEditorAssistSystemPrompt(request, maxAlternatives),
      userPrompt: this.buildEditorAssistUserPrompt(request),
      toolName: "submit_editor_assist",
      toolDescription: "Submit editor assist alternatives in strict schema.",
      schema: EditorAssistResultSchema,
    });

    const alternatives = execution.parsed.alternatives
      .map((alt) => ({
        text: alt.text.trim(),
        confidence: Math.max(0, Math.min(1, Number(alt.confidence))),
        nativeExplanation: alt.nativeExplanation.trim(),
      }))
      .filter((alt) => alt.text.length > 0 && alt.nativeExplanation.length > 0)
      .slice(0, maxAlternatives);

    if (alternatives.length === 0) {
      throw new Error("No editor assist alternatives");
    }

    return {
      command: request.command,
      alternatives,
      meta: {
        provider: execution.provider,
        requestComplexity: execution.requestComplexity,
      },
    };
  }

  async assistEditorWithFallback(
    request: EditorAssistRequest,
    options: { temperature?: number; priority?: LLMRoutingPriority } = {},
  ): Promise<EditorAssistResponse> {
    return this.assistEditor(request, options);
  }

  private async executeWithRouter<
    TSchema extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
  >(args: {
    task: ServiceTask;
    sourceLanguage?: string;
    targetLanguage?: string;
    hasContext?: boolean;
    priority?: LLMRoutingPriority;
    temperature: number;
    maxTokens: number;
    inputText: string;
    contextText?: string;
    maxAlternatives?: number;
    systemPrompt: string;
    userPrompt: string;
    toolName: string;
    toolDescription: string;
    schema: TSchema;
  }): Promise<{
    parsed: v.InferOutput<TSchema>;
    provider: LLMProvider;
    requestComplexity: RequestComplexity;
  }> {
    const providerChain = this.getProviderChain(args);
    const startedAt = Date.now();

    const errors: string[] = [];
    const schemaValidationErrors: string[] = [];
    const requestComplexity = classifyRequestComplexity({
      task: args.task,
      text: args.inputText,
      context: args.contextText,
      maxAlternatives: args.maxAlternatives,
    });

    for (const [index, provider] of providerChain.entries()) {
      try {
        const result = await generateStructuredOutputWithRetry({
          model: this.getModel(provider),
          toolName: args.toolName,
          toolDescription: args.toolDescription,
          schema: args.schema,
          systemPrompt: args.systemPrompt,
          userPrompt: args.userPrompt,
          provider,
          requestComplexity,
          temperature: args.temperature,
          maxTokens: args.maxTokens,
        });

        logger.info("Provider execution succeeded", {
          task: args.task,
          provider,
          fallback_count: index,
          latency_ms: Date.now() - startedAt,
          request_complexity: requestComplexity,
          attempt: result.attempt,
          validation_error_count: result.validationErrors.length,
        });
        return {
          parsed: result.output,
          provider,
          requestComplexity,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        errors.push(`${provider}: ${message}`);
        if (isModelOutputInvalidError(error)) {
          schemaValidationErrors.push(...error.details.validationErrors);
        }
        logger.warn("Provider execution failed", {
          provider,
          task: args.task,
          err: error,
        });
      }
    }

    logger.error("All providers failed", {
      task: args.task,
      fallback_count: Math.max(providerChain.length - 1, 0),
      latency_ms: Date.now() - startedAt,
      request_complexity: requestComplexity,
    });
    if (schemaValidationErrors.length > 0) {
      throw new ModelOutputInvalidError("invalid structured output from model", {
        provider: providerChain.join(","),
        requestComplexity,
        validationErrors: schemaValidationErrors,
      });
    }
    throw new Error(`Service temporarily unavailable. ${errors.join(" | ")}`);
  }

  private getModel(provider: LLMProvider) {
    switch (provider) {
      case "workers": {
        const gatewayBaseURL = this.normalizeOpenAIBaseURL(
          this.config.gatewayBaseURL || this.getEnvValue("CF_AIG_BASE_URL"),
        );
        const workersModel = this.getWorkersModel();
        if (!gatewayBaseURL) {
          throw new Error("CF_AIG_BASE_URL is required for workers provider");
        }
        if (!workersModel) {
          throw new Error("WORKERS_AI_MODEL is required for workers provider");
        }

        const workersClient = this.createGatewayOpenAIClient(gatewayBaseURL);
        return workersClient(`workers-ai/${workersModel}`);
      }
      case "anthropic":
        return anthropic("claude-3-haiku-20240307");
      case "openai": {
        const gatewayBaseURL = this.normalizeOpenAIBaseURL(
          this.config.gatewayBaseURL || this.getEnvValue("CF_AIG_BASE_URL"),
        );
        const openaiBaseURL = this.normalizeOpenAIBaseURL(this.getEnvValue("OPENAI_BASE_URL"));
        const openaiApiKey = this.config.openaiApiKey || this.getEnvValue("OPENAI_API_KEY");
        const openaiModel =
          this.config.openaiModel ||
          this.getEnvValue("OPENAI_MODEL") ||
          this.getEnvValue("LLM_MODEL") ||
          (gatewayBaseURL ? "openai/gpt-4o-mini" : "gpt-4o-mini");
        const useGateway = Boolean(gatewayBaseURL);
        const openaiClient = useGateway
          ? this.createGatewayOpenAIClient(gatewayBaseURL!)
          : createOpenAI({
              apiKey: openaiApiKey,
              baseURL: openaiBaseURL,
            });
        return openaiClient(openaiModel);
      }
      case "zai": {
        const zaiApiKey = this.config.zaiApiKey || this.getEnvValue("ZAI_API_KEY");
        const zaiBaseURL =
          this.config.zaiBaseURL || this.getEnvValue("ZAI_BASE_URL") || "https://api.z.ai/api/paas/v4";
        const zaiModel = this.config.zaiModel || this.getEnvValue("ZAI_MODEL") || "glm-4.5-flash";
        const zaiClient = createOpenAI({
          apiKey: zaiApiKey,
          baseURL: zaiBaseURL,
        });
        return zaiClient(zaiModel);
      }
      case "google":
      default:
        return google("gemini-2.0-flash-exp");
    }
  }

  private isProviderEnabled(provider: LLMProvider): boolean {
    if (provider === "workers") {
      return Boolean(
        (this.config.gatewayBaseURL || this.getEnvValue("CF_AIG_BASE_URL")) && this.getWorkersModel(),
      );
    }

    if (provider === "google") return true;

    if (provider === "anthropic") {
      return Boolean(this.getEnvValue("ANTHROPIC_API_KEY"));
    }

    if (provider === "openai") {
      return Boolean(
        this.config.openaiApiKey ||
          this.getEnvValue("OPENAI_API_KEY") ||
          this.config.gatewayBaseURL ||
          this.getEnvValue("CF_AIG_BASE_URL"),
      );
    }

    if (provider === "zai") {
      return Boolean(this.config.zaiApiKey || this.getEnvValue("ZAI_API_KEY"));
    }

    return false;
  }

  private getEnvValue(key: string): string | undefined {
    const runtime = this.runtimeEnv?.[key];
    if (typeof runtime === "string" && runtime.length > 0) {
      return runtime;
    }

    const configured = this.config.env?.[key];
    if (typeof configured === "string" && configured.length > 0) {
      return configured;
    }

    if (typeof process !== "undefined" && process.env) {
      const fromProcess = process.env[key];
      if (typeof fromProcess === "string" && fromProcess.length > 0) {
        return fromProcess;
      }
    }

    return undefined;
  }

  private normalizeOpenAIBaseURL(baseURL?: string): string | undefined {
    if (!baseURL) return undefined;
    return baseURL.replace(/\/chat\/completions\/?$/i, "");
  }

  private getProviderChain(args: {
    task: ServiceTask;
    sourceLanguage?: string;
    targetLanguage?: string;
    hasContext?: boolean;
    priority?: LLMRoutingPriority;
  }): LLMProvider[] {
    if (args.task === "phonetic_resolve") {
      const phoneticChain: LLMProvider[] = ["workers", "google", "openai"];
      return phoneticChain.filter((provider) => this.isProviderEnabled(provider));
    }

    return this.router.selectProviderChain({
      task: "translate",
      sourceLanguage: args.sourceLanguage,
      targetLanguage: args.targetLanguage,
      hasContext: args.hasContext,
      priority: args.priority || this.config.priority || "quality",
      providerOverride: this.config.provider || "auto",
    });
  }

  private getWorkersModel(): string | undefined {
    const raw = this.getEnvValue("WORKERS_AI_MODEL");
    if (!raw) return undefined;
    return raw.replace(/^workers-ai\//i, "");
  }

  private createGatewayOpenAIClient(gatewayBaseURL: string) {
    const gatewayToken = this.getGatewayToken();
    return createOpenAI({
      apiKey: "cf-aig-placeholder",
      baseURL: gatewayBaseURL,
      headers: gatewayToken ? { "cf-aig-authorization": gatewayToken } : undefined,
      fetch: async (input, init) => {
        const headers = new Headers(init?.headers);
        headers.delete("authorization");
        return fetch(input, { ...init, headers });
      },
    });
  }

  private getGatewayToken(): string | undefined {
    const rawGatewayToken = this.config.gatewayAuthToken || this.getEnvValue("CF_AIG_AUTH_TOKEN");
    return rawGatewayToken?.startsWith("Bearer ")
      ? rawGatewayToken
      : rawGatewayToken
        ? `Bearer ${rawGatewayToken}`
        : undefined;
  }

  private buildTranslateSystemPrompt(targetLanguage: string, includeExplanation: boolean) {
    const explanationNote = includeExplanation
      ? '\nInclude an "explanation" field explaining what was mixed-language and how it was converted.'
      : "\nDo not include an explanation field.";

    return `${this.systemPrompt}

Target Language: ${targetLanguage}${explanationNote}`;
  }

  private buildPhoneticSystemPrompt(request: PhoneticResolveRequest) {
    const contextText = request.context?.trim() || "(none)";
    const uiLanguage = request.uiLanguage || request.sourceLanguage;

    return `${this.phoneticSystemPrompt}

Source language: ${request.sourceLanguage}
Target language: ${request.targetLanguage}
UI language for meaning gloss: ${uiLanguage}
Context sentence: ${contextText}

Rules:
- Return at least 5 candidates when possible.
- Keep meanings short and learner-friendly.
- Prefer common and natural words in the given language pair.`;
  }

  private buildEditorAssistSystemPrompt(request: EditorAssistRequest, maxAlternatives: number) {
    return `${this.editorAssistSystemPrompt}

Command: ${request.command}
Target language: ${request.targetLanguage}
Native language for explanation: ${request.nativeLanguage}
Requested alternatives: ${maxAlternatives}
Tone: ${request.options?.tone || "neutral"}
Length: ${request.options?.length || "same"}

Rules:
- Return exactly the requested number of alternatives when possible.
- Keep explanation concise and easy for learners.
- Avoid markdown or extra keys.`;
  }

  private buildEditorAssistUserPrompt(request: EditorAssistRequest) {
    const selection = request.selection
      ? `Selection start: ${request.selection.start ?? "none"}, end: ${request.selection.end ?? "none"}`
      : "Selection: none";

    return `Text:\n${request.text}\n\n${selection}`;
  }

  private normalizePhoneticInput(input: string): string {
    return input.normalize("NFKC").trim().toLowerCase();
  }

  private postProcessPhoneticCandidates(
    candidates: PhoneticResolveCandidate[],
  ): PhoneticResolveCandidate[] {
    const normalized: PhoneticResolveCandidate[] = candidates
      .map((candidate) => ({
        word: candidate.word.trim(),
        confidence: Math.max(0, Math.min(1, Number(candidate.confidence))),
        meaning: candidate.meaning.trim(),
        reading: candidate.reading?.trim(),
        reason: candidate.reason?.trim(),
      }))
      .filter((candidate) => candidate.word.length > 0 && candidate.meaning.length > 0);

    const deduped = new Map<string, PhoneticResolveCandidate>();
    for (const candidate of normalized) {
      const key = candidate.word.normalize("NFKC").toLowerCase();
      const current = deduped.get(key);
      if (!current || candidate.confidence > current.confidence) {
        deduped.set(key, candidate);
      }
    }

    const sorted = Array.from(deduped.values()).sort((a, b) => b.confidence - a.confidence);
    const primary = sorted.filter((candidate) => candidate.confidence >= 0.75).slice(0, 5);
    if (primary.length >= 3) {
      return primary;
    }

    const secondary = sorted.filter((candidate) => candidate.confidence >= 0.6);
    const result: PhoneticResolveCandidate[] = [];
    for (const candidate of secondary) {
      if (result.length >= 5) break;
      if (!result.some((item) => item.word === candidate.word)) {
        result.push(candidate);
      }
    }

    for (const candidate of sorted) {
      if (result.length >= 3 || result.length >= 5) break;
      if (!result.some((item) => item.word === candidate.word)) {
        result.push(candidate);
      }
    }

    return result.slice(0, 5);
  }
}
